import React from 'react';

import { Box } from '@mui/material';

import { Form, FormApi, FormSpy, useForm, useFormState } from '@hrlogics/react-components/form';
import { SxProp } from '@hrlogics/react-helper-types';

import { CompanyType, StripeAccountStatus } from 'src/api';
import { Button, FullscreenLoader } from 'src/components/common';
import { useCurrentUserPermissions } from 'src/hooks/user';

import { BillingMethodCard } from './components/BillingMethodCard';
import { RevenueShareSettingsFormSection } from './components/RevenueShareSettingsFormSection';
import { RevenueShareFormField } from './constants';
import { RevenueShareFormValues } from './types';
import { revenueShareFormValidationSchema } from './utils';

type RevenueShareFormProps = {
  companyType: CompanyType;
  stripeAccountStatus: StripeAccountStatus;
  initialValues: RevenueShareFormValues;
  handleSubmit: (
    formValues: RevenueShareFormValues,
    formApi: FormApi<RevenueShareFormValues>,
  ) => void | Promise<void>;
  loading: boolean;
};

export const RevenueShareForm: React.FC<RevenueShareFormProps> = ({
  companyType,
  stripeAccountStatus,
  initialValues,
  handleSubmit,
  loading,
}) => {
  const { isPlatformAdmin } = useCurrentUserPermissions();

  if (loading) {
    return <FullscreenLoader />;
  }

  return (
    <Form<RevenueShareFormValues>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      subscription={{}}
      validationSchema={revenueShareFormValidationSchema}
      containerProps={{ sx: formContainerStyles }}
    >
      <BillingMethodCard
        fieldName={RevenueShareFormField.revenueShareEnabled}
        stripeAccountStatus={stripeAccountStatus}
      />

      <FormSpy<RevenueShareFormValues> subscription={{ values: true }}>
        {({ values }) =>
          values.revenueShareEnabled ? (
            <RevenueShareSettingsFormSection companyType={companyType} />
          ) : null
        }
      </FormSpy>

      {isPlatformAdmin ? <RevenueShareFormActions /> : null}
    </Form>
  );
};

const RevenueShareFormActions: React.FC = () => {
  const form = useForm<RevenueShareFormValues>();
  const { submitting, dirty, valid } = useFormState<RevenueShareFormValues>({
    subscription: { submitting: true, dirty: true, valid: true },
  });

  const handleReset = React.useCallback(() => form.reset(), [form]);

  return (
    <Box display="flex" gap={2} justifyContent="center">
      <Button
        type="reset"
        variant="outlined"
        color="error"
        onClick={handleReset}
        disabled={submitting}
      >
        Reset
      </Button>

      <Button
        type="submit"
        variant="contained"
        disabled={submitting || !dirty || !valid}
        loading={submitting}
      >
        Save Changes
      </Button>
    </Box>
  );
};

const formContainerStyles: SxProp = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};
